import Paper from "@mui/material/Paper";
import { convertUTCToLocalDate } from "components/UTC/UtcConverter";
import * as calculation from "helper/Calculate";
import FormService from "Lib/API/Forms";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccessTokenSilently, getAuthenticated } from "utils/functions";

import PaymentInstallment from "components/PaymentInstallment/PaymentInstallment";
import { formatDate } from "helper/dateFormat";
import { numberFormat } from "helper/numberFormat";
import { Class } from "services/api/class";
import {
  DataFoundWrapper,
  Discount,
  Note,
  PayDetails,
  PaymentAmount,
  SummaryItem,
  SummaryItemCharge,
  SummaryItemInfo,
  SummaryItemName,
  SummaryTitle,
  TotalDueAmount,
  TotalPayment,
  Wrapper,
} from "./Theme";
import { Fonts, FontColors } from "theme/fontConfig";
export const SummaryCard = ({
  handleTotalForPay,
  totalPaymentWithCVFee,
  dueAmountCF,
  totalDueAmountWithCF,
  totalDiscount,
  dueAmountTotalDiscount,
  isInstallmentEnabled,
  setActivePaymentAfter,
  newProducts,
  setProducts,
  activePayment,
  setConvenienceFeeByPayment,
  convenienceFeeByPayment,
  currentPaymentMethod,
  donationAmount,
  onCalcDueAmountChange,
  onCalcPriceStatusChange,
}) => {
  const {
    products,
    classDetails,
    varients,
    numberOfTickets,
    totalProducts,
  } = useSelector((state) => state?.Class);

  const totalPay = handleTotalForPay();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const isAuthenticated = getAuthenticated();

  const countAmount = (numberOfProduct, amount) => {
    const result = numberOfProduct * amount;
    return numberFormat({ number: +result, decimal: 2, style: "currency" });
  };

  useEffect(() => {
    const processedData = processInputData(varients);
    const totalAmount = processedData?.reduce(
      (sum, obj) => sum + obj.amount,
      0,
    );
    const finalAmount = Number.isNaN(totalAmount) ? 0 : totalAmount;
    dispatch(Class.actions.setTotalProducts(finalAmount));
    setProducts((prev) =>
      processedData.map((item) => {
        const existing = (prev || []).find((p) => p.priceId === item.priceId);
        if (!existing) return item;
        return {
          ...item,
          installmentStatus: existing.installmentStatus,
          installment: existing.installment,
        };
      })
    );
  }, [dispatch, varients]);

  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [apiDueAmount, setApiDueAmount] = useState(null);
  const [apiTotalAmount, setApiTotalAmount] = useState(null);
  const [apiConvenienceFee, setApiConvenienceFee] = useState(null);
  const [apiTotalDiscount, setApiTotalDiscount] = useState(null);
  const [fetchingDiscount, setFetchingDiscount] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);
  const triggerRefetch = () => setRefetchTrigger((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    setApiDueAmount(null);
    setApiTotalAmount(null);
    setApiConvenienceFee(null);
    setApiTotalDiscount(null);
    setFetchingDiscount(true);
    setApiFailed(false);
    const fetchCalcPrice = async () => {
      if (!classDetails?._id) return;
      try {
        const token =
          (await getAccessTokenSilently()) ||
          localStorage.getItem("schoolToken") ||
          localStorage.getItem("formToken");
        if (!token || cancelled) return;

        const prices = (classDetails?.class_pricing?.prices) || [];
        const installments = [];
        prices.forEach((p) => {
          if (p?.installmentStatus && p?.installment?.value) {
            installments.push({ priceId: p._id, numberOfInstallment: p.installment.value });
          }
        });

        const products = Object.values(varients || {})
          .filter((item) => {
            if (item.variantsInfo && item.variantsInfo.length > 0) {
              return item.variantsInfo.some((v) => v.counterValue > 0);
            }
            return item.counterValue > 0;
          })
          .map((item) => {
            const matchedProduct = (newProducts || []).find((p) => p.priceId === item.id);
            const hasInstallment = matchedProduct?.installmentStatus && matchedProduct?.installment?.value;
            if (item.variantsInfo && item.variantsInfo.length > 0) {
              const variant = {};
              item.variantsInfo.forEach((v) => {
                if (v.counterValue > 0) variant[v.option] = v.counterValue;
              });
              return {
                id: item.id,
                title: item.varientTitle,
                variant,
                ...(hasInstallment ? { numberOfInstallments: matchedProduct.installment.value } : {}),
              };
            }
            return {
              id: item.id,
              title: item.varientTitle,
              quantity: item.counterValue,
              ...(hasInstallment ? { numberOfInstallments: matchedProduct.installment.value } : {}),
            };
          });

        const body = {
          classId: classDetails._id,
          paymentMethodId: currentPaymentMethod?._id,
          coverFee: convenienceFeeByPayment?.[activePayment] ?? true,
          isMember: isAuthenticated,
          installments,
          products,
          checkoutDonation: { amount: donationAmount || 0 },
        };

        const res = await FormService.calculateSchoolPrice(token, body);
        if (!cancelled) {
          const result = res?.result ?? res;
          onCalcPriceStatusChange && onCalcPriceStatusChange(false);
          onCalcDueAmountChange && onCalcDueAmountChange(result?.dueAmount);
          setApiDueAmount(result?.dueAmount ?? null);
          setApiTotalAmount(result?.totalAmount ?? null);
          setApiConvenienceFee(result?.convenienceFee ?? null);
          setApiTotalDiscount(result?.totalDiscount ?? null);
          setFetchingDiscount(false);
          setApiFailed(false);
        }
      } catch (e) {
        if (!cancelled) {
          onCalcPriceStatusChange && onCalcPriceStatusChange(true);
          setFetchingDiscount(false);
          setApiFailed(true);
          enqueueSnackbar("Failed to fetch prices", { variant: "error" });
        }
      }
    };
    fetchCalcPrice();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPaymentWithCVFee, refetchTrigger]);

  const processInputData = (data) => {
    const processedProducts =
      data && Object.values(data)?.length > 0
        ? Object.values(data)?.map((item, index) => {
            if (item.variantsInfo && item.variantsInfo.length > 0) {
              const totalCounterSum = item.variantsInfo.reduce(
                (sum, obj) => sum + obj.counterValue,
                0,
              );
              let totalAmount = 0;
              // const totalCounterSumAmount = item.variantsInfo.reduce((sum, obj) => sum + obj.amount, 0);
              if (item.variantsInfo && item.variantsInfo.length > 0) {
                item.variantsInfo.forEach((variant) => {
                  totalAmount += variant.counterValue * variant.amount;
                });
                return {
                  title: item.varientTitle,
                  total: totalCounterSum,
                  amount: totalAmount,
                  type: item.type,
                  priceId: item.id,
                };
              }
            } else {
              if (item.type === "openAmount") {
                return {
                  title: item.varientTitle,
                  total: item.counterValue || 0,
                  amount: item.price ? item.price * item.counterValue : 0,
                  type: item.type,
                  priceId: item.id,
                };
              } else {
                return {
                  title: item.varientTitle,
                  total: item.counterValue || 0,
                  amount: item.amount * item.counterValue,
                  type: item.type,
                  priceId: item.id,
                };
              }
            }
          })
        : [];
    return processedProducts;
  };

  const calculateInstallmentAmount = (item) => {
    if (!item?.installmentStatus) return "";
    if (item?.installment?.value) {
      const itemAmount = item?.amount;
      return numberFormat({
        number: +itemAmount / item?.installment?.value,
        decimal: 2,
        style: "currency",
      });
    }
    return "";
  };

  const enableProductInstallment = (item) => {
    const currentPrice = classDetails?.class_pricing?.prices?.find(
      (price) => price?._id === item?.priceId,
    );
    return currentPrice || {};
  };
  const shouldShowConvenienceFeeToggle =
    currentPaymentMethod?.coverConvenienceFee;
  const isConvenienceFeeOptional =
    currentPaymentMethod?.convenienceFeeInfo?.optional === true;

  const spinStyle = { animation: "spin 1s linear infinite" };
  if (typeof document !== "undefined" && !document.getElementById("__school-spin-style")) {
    const s = document.createElement("style");
    s.id = "__school-spin-style";
    s.textContent = "@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }";
    document.head.appendChild(s);
  }

  return (
    <>
      {(totalPay?.total !== "0.00" || totalProducts > 0 || totalDiscount > 0) && (
        <Paper
          sx={{
            minHeight: "278px",
            // marginTop: "33px",
            borderRadius: "10px",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#1A2027" : "#fff",
          }}
        >
          <Wrapper>
            <SummaryTitle>
              <span>Summary</span>
            </SummaryTitle>
            <DataFoundWrapper>
              <PayDetails>
                {classDetails?.class_pricing?.prices?.map(
                  (item, index) =>
                    item?.type === "onRegistration" && (
                      <SummaryItem>
                        <div>
                          <li key={index}>
                            {item?.title}
                            <span>
                              {numberFormat({
                                number: +item.amount,
                                decimal: 2,
                                style: "currency",
                              })}
                            </span>
                          </li>
                          <p>{item?.note}</p>
                        </div>
                        {isInstallmentEnabled &&
                          item?.isInstallmentTransaction && (
                            <PaymentInstallment
                              onAllowInstallmentChange={(e) => {
                                const { checked } = e.target;
                                const updatedClassPricing = classDetails?.class_pricing?.prices?.map(
                                  (val) => {
                                    if (val?._id === item?._id) {
                                      return {
                                        ...val,
                                        installmentStatus: checked,
                                        installment: null,
                                      };
                                    }
                                    return val;
                                  },
                                );
                                const updatedClassDetails = {
                                  ...classDetails,
                                  class_pricing: {
                                    ...classDetails?.class_pricing,
                                    prices: updatedClassPricing,
                                  },
                                };
                                dispatch(
                                  Class.actions.setClassDetails(
                                    updatedClassDetails,
                                  ),
                                );
                                setActivePaymentAfter();
                                triggerRefetch();
                              }}
                              installmentValue={item?.installmentStatus}
                              onSelectInstallment={(value) => {
                                const updatedClassPricing = classDetails?.class_pricing?.prices?.map(
                                  (val) => {
                                    if (val?._id === item?._id) {
                                      return {
                                        ...val,
                                        installment: value,
                                      };
                                    }
                                    return val;
                                  },
                                );
                                const updatedClassDetails = {
                                  ...classDetails,
                                  class_pricing: {
                                    ...classDetails?.class_pricing,
                                    prices: updatedClassPricing,
                                  },
                                };
                                dispatch(
                                  Class.actions.setClassDetails(
                                    updatedClassDetails,
                                  ),
                                );
                                triggerRefetch();
                              }}
                              selectedInstallment={item?.installment}
                              installmentAmount={calculateInstallmentAmount(
                                item,
                              )}
                              maxInstallment={
                                item?.installmentDetailsTransaction
                                  ?.numberOfInstallment
                              }
                            />
                          )}
                      </SummaryItem>
                    ),
                )}
                {newProducts.map((product, index) => (
                  <>
                    {product?.total > 0 && product?.amount > 0 && (
                      <SummaryItem>
                        <SummaryItemCharge>
                          <SummaryItemInfo>
                            <SummaryItemName>
                              {product?.total} {product?.title}
                            </SummaryItemName>
                          </SummaryItemInfo>
                          {`${numberFormat({
                            number: product?.amount,
                            decimal: 2,
                            style: "currency",
                          })}`}
                        </SummaryItemCharge>
                        {isInstallmentEnabled &&
                          enableProductInstallment(product)
                            ?.isInstallmentTransaction && (
                            <PaymentInstallment
                              onAllowInstallmentChange={(e) => {
                                const { checked } = e.target;
                                const updatedNewProducts = newProducts?.map(
                                  (val) => {
                                    if (val?.title === product?.title) {
                                      return {
                                        ...val,
                                        installmentStatus: checked,
                                        ...(!checked && { installment: null }),
                                      };
                                    }
                                    return val;
                                  },
                                );
                                setProducts(updatedNewProducts);
                                setActivePaymentAfter();
                                triggerRefetch();
                              }}
                              installmentValue={
                                product?.installmentStatus || false
                              }
                              onSelectInstallment={(value) => {
                                const updatedNewProducts = newProducts?.map(
                                  (val) => {
                                    if (val?.title === product?.title) {
                                      return {
                                        ...val,
                                        installment: value,
                                      };
                                    }
                                    return val;
                                  },
                                );
                                setProducts(updatedNewProducts);
                                triggerRefetch();
                              }}
                              selectedInstallment={product?.installment}
                              installmentAmount={calculateInstallmentAmount(
                                product,
                                "product",
                              )}
                            />
                          )}
                      </SummaryItem>
                    )}
                  </>
                ))}
              </PayDetails>

              {/* {newProducts.map((product, index) => (
              <SummaryItem>
                {product?.total > 0 &&
                  <SummaryItemCharge>
                    <SummaryItemInfo>
                      <SummaryItemName>{product?.total} {product?.title}</SummaryItemName>
                    </SummaryItemInfo>
                    <PaymentAmount>
                      {`${products?.[index]?.tax_type}
                            ${countAmount(product?.total, product?.amount)}`}
                    </PaymentAmount>
                  </SummaryItemCharge>}

              </SummaryItem>
            ))}<br /> */}
              {(fetchingDiscount || (apiTotalDiscount !== null ? apiTotalDiscount > 0 : totalDiscount > 0)) && (
                <PayDetails>
                  <li>
                    Total Discount
                    <span>
                      {fetchingDiscount ? (
                        <svg style={spinStyle} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                      ) : apiFailed ? (
                        <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "3px 8px", cursor: "pointer" }}>↻ Retry</button>
                      ) : numberFormat({
                        number: +(apiTotalDiscount !== null ? apiTotalDiscount : totalDiscount),
                        decimal: 2,
                        style: "currency",
                      })}
                    </span>
                  </li>
                </PayDetails>
              )}
              {shouldShowConvenienceFeeToggle && (
                <SummaryItem>
                        {convenienceFeeByPayment[activePayment] && (
                    <SummaryItemCharge>
                      <SummaryItemInfo>
                        <SummaryItemName
                          style={{ color: FontColors.Blue002E5C, fontSize: Fonts.GeneralAmountFont.fontSize }}
                        >
                          Convenience fee
                        </SummaryItemName>
                      </SummaryItemInfo>
                      <PaymentAmount>
                        {fetchingDiscount ? (
                          <svg style={spinStyle} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                        ) : apiFailed ? (
                          <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "3px 8px", cursor: "pointer" }}>↻ Retry</button>
                        ) : numberFormat({
                          number: apiConvenienceFee !== null ? +apiConvenienceFee : +dueAmountCF,
                          decimal: 2,
                          style: "currency",
                        })}
                      </PaymentAmount>
                    </SummaryItemCharge>
                  )}
                  {isConvenienceFeeOptional && (
                    <div
                      className={`wrapper check-box__wrapper`}
                      style={{
                        display: "flex",
                        // marginBottom: "10px",
                        marginTop: "10px",
                                                paddingTop:"10px",
                        color: FontColors.Slate6B7280,
                        borderTop: "1px dashed rgba(0, 46, 92, 0.10)"
                      }}
                    >
                      <label className="check-box-container" style={{position:"relative"}}>
                        <input
                          type="checkbox"
                          style={{ marginRight: 8 }}
                          checked={convenienceFeeByPayment[activePayment]}
                          onChange={(e) => {
                            setConvenienceFeeByPayment((prev) => ({
                              ...prev,
                              [activePayment]: e.target.checked,
                            }));
                          }}

                          // disabled={disabled}
                          // {...rest}
                        />
                        <span className={"checkbox-checkmark"}></span>
                      </label>
                      <span className="text">
                        {currentPaymentMethod?.convenienceFeeInfo?.message}
                      </span>
                    </div>
                  )}
                  {/* {convenienceFeeByPayment[activePayment] && (
                    <SummaryItemCharge>
                      <SummaryItemInfo>
                        <SummaryItemName
                          style={{ color: "#002e5c", fontSize: "14px" }}
                        >
                          Convenience fee
                        </SummaryItemName>
                      </SummaryItemInfo>
                      <PaymentAmount>
                        {numberFormat({
                          number: +dueAmountCF,
                          decimal: 2,
                          style: "currency",
                        })}
                      </PaymentAmount>
                    </SummaryItemCharge>
                  )} */}
                </SummaryItem>
              )}
              <TotalPayment>
                <p>Payment Total</p>
                <span>
                  {fetchingDiscount ? (
                    <svg style={spinStyle} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                  ) : apiFailed ? (
                    <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "3px 8px", cursor: "pointer" }}>↻ Retry</button>
                  ) : numberFormat({
                    number: apiTotalAmount !== null ? +apiTotalAmount : +totalPaymentWithCVFee,
                    decimal: 2,
                    style: "currency",
                  })}
                </span>
              </TotalPayment>
              <TotalDueAmount>
                <p>
                  {dueAmountTotalDiscount > 0
                    ? "Due Now After Discount"
                    : "Due Now"}
                </p>
                <span>
                  {fetchingDiscount ? (
                    <svg style={spinStyle} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                  ) : apiFailed ? (
                    <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "3px 8px", cursor: "pointer" }}>↻ Retry</button>
                  ) : numberFormat({
                    number: apiDueAmount !== null ? +apiDueAmount : +totalDueAmountWithCF,
                    decimal: 2,
                    style: "currency",
                  })}
                </span>
              </TotalDueAmount>
            </DataFoundWrapper>
          </Wrapper>
        </Paper>
      )}
    </>
  );
};
